import { useEditor, EditorContent } from "@tiptap/react";
import { StarterKit } from "@tiptap/starter-kit";
import { Link } from "@tiptap/extension-link";
import { Image } from "@tiptap/extension-image";
import { Table } from "@tiptap/extension-table";
import { TableRow } from "@tiptap/extension-table-row";
import { TableHeader } from "@tiptap/extension-table-header";
import { TableCell } from "@tiptap/extension-table-cell";
import { Placeholder } from "@tiptap/extension-placeholder";
import { Underline } from "@tiptap/extension-underline";
import { useEffect, useState } from "react";
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  Strikethrough,
  Heading1,
  Heading2,
  Heading3,
  List,
  ListOrdered,
  Quote,
  Code,
  Link as LinkIcon,
  Image as ImageIcon,
  Table as TableIcon,
  Undo,
  Redo,
  Minus,
  Code2,
} from "lucide-react";

const ToolBtn = ({ onClick, active, title, children }) => (
  <button
    type="button"
    onClick={onClick}
    title={title}
    className={`p-1.5 rounded hover:bg-slate-100 transition ${active ? "bg-slate-200 text-prepify-700" : "text-slate-600"}`}
  >
    {children}
  </button>
);

/**
 * RichTextEditor — WYSIWYG editor powered by TipTap with HTML-mode toggle.
 * Props:
 *  - value: current HTML string
 *  - onChange(html): called on every edit (debounced externally if needed)
 *  - onOpenMediaPicker(): callback invoked when user clicks the image button
 *  - placeholder: placeholder text
 */
const RichTextEditor = ({ value = "", onChange, onOpenMediaPicker, placeholder = "Start writing your post…" }) => {
  const [mode, setMode] = useState("visual"); // 'visual' | 'html'
  const [rawHtml, setRawHtml] = useState(value || "");

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3] }, link: false, underline: false }),
      Underline,
      Link.configure({ openOnClick: false, HTMLAttributes: { class: "text-prepify-600 underline" } }),
      Image.configure({ HTMLAttributes: { class: "rounded-lg my-4" } }),
      Table.configure({ resizable: true }),
      TableRow,
      TableHeader,
      TableCell,
      Placeholder.configure({ placeholder }),
    ],
    content: value || "",
    onUpdate: ({ editor: ed }) => {
      const html = ed.getHTML();
      setRawHtml(html);
      onChange?.(html);
    },
    editorProps: {
      attributes: {
        class:
          "prose prose-slate max-w-none min-h-[400px] p-5 focus:outline-none prose-headings:font-bold prose-a:text-prepify-600 prose-img:rounded-lg",
      },
    },
  });

  // If external value changes (e.g., after autosave or revision restore), sync it in
  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    if (value && value !== current) {
      editor.commands.setContent(value, false);
      setRawHtml(value);
    }
  }, [value, editor]);

  if (!editor) return <div className="text-slate-400 text-sm p-4">Loading editor…</div>;

  const insertLink = () => {
    const prev = editor.getAttributes("link").href;
    const url = window.prompt("Link URL", prev || "https://");
    if (url === null) return;
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };

  const switchToHtml = () => {
    setRawHtml(editor.getHTML());
    setMode("html");
  };
  const switchToVisual = () => {
    editor.commands.setContent(rawHtml || "", false);
    onChange?.(rawHtml || "");
    setMode("visual");
  };

  return (
    <div className="border border-slate-200 rounded-xl overflow-hidden bg-white">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-0.5 px-2 py-1.5 border-b border-slate-200 bg-slate-50">
        <ToolBtn onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} active={editor.isActive("heading", { level: 1 })} title="Heading 1"><Heading1 size={16} /></ToolBtn>
        <ToolBtn onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} active={editor.isActive("heading", { level: 2 })} title="Heading 2"><Heading2 size={16} /></ToolBtn>
        <ToolBtn onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} active={editor.isActive("heading", { level: 3 })} title="Heading 3"><Heading3 size={16} /></ToolBtn>
        <span className="w-px h-5 bg-slate-200 mx-1" />
        <ToolBtn onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive("bold")} title="Bold"><Bold size={16} /></ToolBtn>
        <ToolBtn onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive("italic")} title="Italic"><Italic size={16} /></ToolBtn>
        <ToolBtn onClick={() => editor.chain().focus().toggleUnderline().run()} active={editor.isActive("underline")} title="Underline"><UnderlineIcon size={16} /></ToolBtn>
        <ToolBtn onClick={() => editor.chain().focus().toggleStrike().run()} active={editor.isActive("strike")} title="Strikethrough"><Strikethrough size={16} /></ToolBtn>
        <span className="w-px h-5 bg-slate-200 mx-1" />
        <ToolBtn onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive("bulletList")} title="Bulleted list"><List size={16} /></ToolBtn>
        <ToolBtn onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive("orderedList")} title="Ordered list"><ListOrdered size={16} /></ToolBtn>
        <ToolBtn onClick={() => editor.chain().focus().toggleBlockquote().run()} active={editor.isActive("blockquote")} title="Blockquote"><Quote size={16} /></ToolBtn>
        <ToolBtn onClick={() => editor.chain().focus().toggleCodeBlock().run()} active={editor.isActive("codeBlock")} title="Code block"><Code size={16} /></ToolBtn>
        <ToolBtn onClick={() => editor.chain().focus().setHorizontalRule().run()} title="Horizontal rule"><Minus size={16} /></ToolBtn>
        <span className="w-px h-5 bg-slate-200 mx-1" />
        <ToolBtn onClick={insertLink} active={editor.isActive("link")} title="Insert / edit link"><LinkIcon size={16} /></ToolBtn>
        <ToolBtn onClick={() => onOpenMediaPicker?.((url) => editor.chain().focus().setImage({ src: url }).run())} title="Insert image from library"><ImageIcon size={16} /></ToolBtn>
        <ToolBtn onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()} title="Insert table"><TableIcon size={16} /></ToolBtn>
        <span className="w-px h-5 bg-slate-200 mx-1" />
        <ToolBtn onClick={() => editor.chain().focus().undo().run()} title="Undo"><Undo size={16} /></ToolBtn>
        <ToolBtn onClick={() => editor.chain().focus().redo().run()} title="Redo"><Redo size={16} /></ToolBtn>
        <span className="flex-1" />
        <button
          type="button"
          onClick={mode === "visual" ? switchToHtml : switchToVisual}
          className={`text-xs font-medium px-3 py-1 rounded-md inline-flex items-center gap-1.5 transition ${mode === "html" ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}
          data-testid="toggle-html-mode-btn"
        >
          <Code2 size={12} /> {mode === "visual" ? "HTML" : "Visual"}
        </button>
      </div>

      {/* Editor body */}
      {mode === "visual" ? (
        <EditorContent editor={editor} data-testid="tiptap-editor" />
      ) : (
        <textarea
          value={rawHtml}
          onChange={(e) => {
            setRawHtml(e.target.value);
            onChange?.(e.target.value);
          }}
          className="w-full min-h-[400px] p-5 font-mono text-[13px] text-slate-800 focus:outline-none"
          spellCheck={false}
          data-testid="html-mode-textarea"
        />
      )}
    </div>
  );
};

export default RichTextEditor;
